import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Plus, Pencil, CalendarClock, ListTodo, Receipt, Download, FileText, Check, AlertTriangle, X } from 'lucide-react';
import api from '../../../utils/api';
import {
  Panel, Button, Pill, RowMenu, DataTable, Empty, Checkbox, Input, ConfirmModal, ReasonModal, LoadingBlock, ErrorBlock, Money,
  useLoad, errMsg, fmtDate, downloadFile,
} from '../kit';
import ParticipantNotes from '../workflow/ParticipantNotes';
import { REPORT_TYPES, CLAIM_KIND, fullName, daysFromToday, inText, LinkButton } from './shared';

// Notes & Billing tab: the time-entry table across the full width (it needs the room for its
// columns), then the participant's tasks and key dates side by side (they were the old Overview tab),
// and the per-participant claims made before company claiming.
export default function NotesTab({ detail, perms, participantName, onEditKeyDates, onFromEmail, onChanged }) {
  const id = detail.id;
  return (
    <div className="space-y-5">
      <ParticipantNotes coordinationId={id} clientId={detail.client_id} participantName={participantName} budgets={detail.budgets || []} services={detail.services || []}
        perms={perms} onFromEmail={onFromEmail} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <div className="min-w-0"><TasksPanel scId={id} /></div>
        <div className="min-w-0"><KeyDatesPanel detail={detail} onEdit={onEditKeyDates} /></div>
      </div>
      {(detail.claims || []).length > 0 && <EarlierClaims claims={detail.claims} onChanged={onChanged} />}
    </div>
  );
}

// ── Tasks ────────────────────────────────────────────────────────────────────────────────────
function TasksPanel({ scId }) {
  const l = useLoad(`/support-coordination/${scId}/tasks`);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const tasks = useMemo(() => l.data || [], [l.data]);
  const open = tasks.filter((t) => t.status !== 'done');
  const done = tasks.filter((t) => t.status === 'done');
  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try { await api.post(`/support-coordination/${scId}/tasks`, { title: title.trim() }); setTitle(''); l.reload(); }
    catch (err) { toast.error(errMsg(err, 'The task wasn’t added. Try again.')); } finally { setBusy(false); }
  };
  const toggle = async (t) => {
    try { await api.patch(`/support-coordination/tasks/${t.id}`, { status: t.status === 'done' ? 'open' : 'done' }); l.reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const row = (t) => {
    const isDone = t.status === 'done';
    const due = daysFromToday(t.due_date);
    const late = !isDone && due != null && due < 0;
    const who = fullName(t.assignee_first, t.assignee_last);
    return (
      <li key={t.id} className="flex items-start gap-3 py-2">
        <span className="pt-0.5"><Checkbox checked={isDone} onChange={() => toggle(t)} aria-label={isDone ? `Reopen: ${t.title}` : `Mark done: ${t.title}`} /></span>
        <div className="min-w-0 flex-1">
          <p className={clsx('text-[13px] leading-snug', isDone ? 'text-slate-500 line-through' : 'text-slate-800')}>{t.title}</p>
          {(t.due_date || who) && (
            <p className="text-[12px] text-slate-500 mt-0.5">
              {t.due_date && <span className={clsx(late && 'font-semibold text-rose-700')}>{late ? `Due ${fmtDate(t.due_date)}, ${inText(due)}` : `Due ${fmtDate(t.due_date)}`}</span>}
              {t.due_date && who && ' · '}{who}
            </p>
          )}
        </div>
      </li>
    );
  };
  return (
    <Panel icon={ListTodo} title="Tasks" sub={l.data ? (open.length ? `${open.length} open` : 'Nothing open') : undefined}>
      <form onSubmit={add} className="flex items-center gap-2 mb-2">
        <label htmlFor={`task-${scId}`} className="sr-only">New task</label>
        <Input id={`task-${scId}`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a task" className="flex-1" />
        <Button type="submit" variant="secondary" size="sm" icon={Plus} loading={busy} disabled={!title.trim()}>Add</Button>
      </form>
      {l.error ? <ErrorBlock error={l.error} onRetry={l.reload} />
        : !l.data ? <LoadingBlock rows={2} />
        : !tasks.length ? <p className="text-[13px] text-slate-500 py-2">No tasks yet. Add follow-ups, callbacks and reviews here.</p>
        : (
          <>
            <ul className="divide-y divide-slate-100">{open.map(row)}</ul>
            {done.length > 0 && (
              <div className="pt-2">
                <button type="button" onClick={() => setShowDone((v) => !v)} aria-expanded={showDone}
                  className="text-[12.5px] font-semibold text-slate-600 hover:text-slate-900 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                  {showDone ? 'Hide' : 'Show'} {done.length} done
                </button>
                {showDone && <ul className="divide-y divide-slate-100 mt-1">{done.map(row)}</ul>}
              </div>
            )}
          </>
        )}
    </Panel>
  );
}

// ── Key dates ────────────────────────────────────────────────────────────────────────────────
function KeyDatesPanel({ detail, onEdit }) {
  const reports = useLoad(`/support-coordination/${detail.id}/reports`);
  const consents = useLoad(`/support-coordination/${detail.id}/consents`);
  const rows = useMemo(() => {
    const open = (reports.data || []).filter((r) => r.status !== 'submitted' && r.due_date);
    // A report in the register due on the progress report date is that report: list it once.
    const reportDay = String(detail.progress_report_due || '').slice(0, 10);
    const sameDay = open.some((r) => String(r.due_date).slice(0, 10) === reportDay);
    return [
      { label: 'Plan review', date: detail.plan_review_due },
      { label: 'Progress report', date: sameDay ? null : detail.progress_report_due },
      { label: detail.next_release_info || 'Funding release', date: detail.next_release_date },
      { label: 'Plan ends', date: detail.plan_end, end: true },
      ...open.map((r) => ({ label: `${REPORT_TYPES[r.report_type] || 'NDIA'} report`, date: r.due_date })),
      ...(consents.data || []).filter((c) => c.status === 'active' && c.expires_at).map((c) => ({ label: `Consent ends: ${c.consent_type}`, date: c.expires_at, end: true })),
    ].filter((r) => r.date).sort((a, b) => String(a.date).localeCompare(String(b.date))).slice(0, 8);
  }, [detail, reports.data, consents.data]);
  return (
    <Panel icon={CalendarClock} title="Key dates" actions={<LinkButton icon={Pencil} onClick={onEdit}>Edit</LinkButton>}>
      {!rows.length ? (
        <p className="text-[13px] text-slate-500">No key dates yet. Add the plan review and progress report dates so nothing sneaks up.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((r, i) => {
            const days = daysFromToday(r.date);
            const past = days != null && days < 0;
            // A date that has simply passed (a plan or consent ending) is a fact, not a late job.
            const late = past && !r.end;
            const soon = !past && days != null && days <= 30;
            return (
              <li key={i} className="py-2 flex items-start justify-between gap-3">
                <span className="text-[13px] text-slate-800 min-w-0">{r.label}</span>
                <span className="text-right flex-shrink-0">
                  <span className="block text-[12.5px] text-slate-700 tabular-nums">{fmtDate(r.date)}</span>
                  <span className={clsx('block text-[12px] tabular-nums', late ? 'font-semibold text-rose-700' : soon ? 'font-semibold text-amber-800' : 'text-slate-500')}>
                    {past && r.end ? 'passed' : inText(days)}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

// ── Earlier claims (made on the file before company claiming) ────────────────────────────────
function EarlierClaims({ claims, onChanged }) {
  const [paying, setPaying] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const short = (cl) => String(cl.id || '').slice(0, 8);
  const download = async (cl, kind) => {
    try {
      if (kind === 'accounting') await downloadFile(`/support-coordination/claims/${cl.id}/accounting-csv`, { filename: `cos-invoice-${short(cl)}.csv` });
      else { await downloadFile(`/support-coordination/claims/${cl.id}/csv`, { filename: `cos-claim-${short(cl)}.csv` }); onChanged?.(); }
    } catch (e) { toast.error(errMsg(e, 'The file didn’t download. Try again.')); }
  };
  const live = (cl) => !['paid', 'cancelled', 'rejected'].includes(cl.status);
  const cols = [
    { id: 'period', header: 'Period', accessorFn: (c) => c.period_from || '', cell: ({ row: { original: c } }) => (
      <span className="whitespace-nowrap">{c.period_from ? `${fmtDate(c.period_from)} – ${fmtDate(c.period_to)}` : 'All draft notes'}</span>
    ) },
    { id: 'kind', header: 'Type', accessorFn: (c) => CLAIM_KIND[c.claim_kind] || c.claim_kind },
    { accessorKey: 'note_count', header: 'Notes', meta: { align: 'right' } },
    { accessorKey: 'total_amount', header: 'Total', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} className="font-semibold" /> },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Pill status={getValue()} size="xs" /> },
    { id: 'menu', header: '', enableSorting: false, meta: { width: 44 }, cell: ({ row: { original: c } }) => (
      <RowMenu label="Claim actions" items={[
        { label: 'Download claim file', icon: FileText, onClick: () => download(c, 'claim') },
        c.claim_kind === 'cos_invoice' && { label: 'Download accounting file (Xero, MYOB)', icon: Download, onClick: () => download(c, 'accounting') },
        live(c) && 'sep',
        live(c) && { label: 'Mark paid', icon: Check, onClick: () => setPaying(c) },
        live(c) && { label: 'Mark rejected by the NDIA', icon: AlertTriangle, onClick: () => setRejecting(c) },
        !['paid', 'cancelled'].includes(c.status) && { label: 'Cancel claim', icon: X, danger: true, onClick: () => setCancelling(c) },
      ]} />
    ) },
  ];
  return (
    <Panel icon={Receipt} hue="slate" title="Earlier claims"
      sub="Claims made on this file before company claiming. They can still be marked paid, rejected or cancelled."
      actions={<Link to="/support-coordination?view=claims" className="text-[13px] font-semibold text-indigo-700 hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">Go to Claims</Link>}>
      <DataTable columns={cols} data={claims} dense empty={<Empty title="No earlier claims" />} />
      <ConfirmModal open={!!paying} onClose={() => setPaying(null)} title="Mark this claim paid?" confirmLabel="Mark paid"
        onConfirm={async () => { await api.patch(`/support-coordination/claims/${paying.id}/paid`); toast.success('Claim marked paid'); onChanged?.(); }}>
        Its notes become paid and locked.
      </ConfirmModal>
      <ConfirmModal open={!!cancelling} onClose={() => setCancelling(null)} title="Cancel this claim?" danger confirmLabel="Cancel claim"
        onConfirm={async () => { await api.patch(`/support-coordination/claims/${cancelling.id}/cancel`); toast.success('Claim cancelled'); onChanged?.(); }}>
        Its notes go back to draft so they can be claimed again.
      </ConfirmModal>
      <ReasonModal open={!!rejecting} onClose={() => setRejecting(null)} title="Mark claim rejected" label="The NDIA’s reason"
        sub="Its notes go back to draft so you can fix them and claim again." confirmLabel="Mark rejected" danger
        onConfirm={async (reason) => { await api.patch(`/support-coordination/claims/${rejecting.id}/reject`, { rejection_reason: reason }); toast.success('Claim marked rejected'); onChanged?.(); }} />
    </Panel>
  );
}

